import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Shared fund-selection logic for the four benchmark ranges (P / R / Dist / MM).
 *
 * WHY THIS EXISTS — the buy-back lock-out
 * ---------------------------------------
 * An exit is a soft delete: the schema's convention is an explicit `weight_pct = 0` row at the
 * exit date in the range's weight table (nothing is ever deleted, because every historical
 * reconstruction reads "the latest row per fund on or before date X" — see
 * src/lib/blendedPortfolioMatrix.ts).
 *
 * `ba_cbam_funds.is_current` is NOT a stored latch — it is DERIVED. `trg_sync_is_current` fires
 * on every insert/update/delete of ba_cbam_fund_weights, ba_r_cbam_fund_weights and
 * ba_dist_cbam_fund_weights and recomputes it as `ba_fund_has_active_weight(fund_id)`, i.e.
 * "any non-zero weight at the fund's own MAX(effective_date), in P or R or Dist".
 *
 * Two consequences drive the design here:
 *
 *  1. The database can and does flip `is_current` back to true on its own — writing a non-zero
 *     weight row at or after the fund's latest effective date is all it takes. So a re-buy must
 *     NEVER hand-write `is_current`; it just writes the weight row and lets the trigger derive
 *     the flag. Hand-writing it would be clobbered by the next weight write anyway, and would be
 *     actively wrong for a BACK-DATED re-buy (a buy dated before the exit leaves the fund
 *     genuinely not-currently-held, and the trigger correctly keeps the flag false).
 *
 *     The lock-out was therefore never a database problem: it was that the fund pickers filtered
 *     on `is_current = true`, so the one fund you needed to select in order to write that row was
 *     the one fund the dropdown hid. Chicken and egg, in the UI only.
 *
 *  2. `ba_fund_has_active_weight` does NOT look at `ba_mm_cbam_fund_weights`, and that table
 *     carries no `trg_sync_is_current` trigger. So `is_current` says nothing whatsoever about the
 *     MM book: an MM-only holding reads as "Legacy" while it is being held. Filtering the MM
 *     picker on `is_current` hid most of the live MM book, not just exited funds.
 *
 * So selection is resolved from the range's OWN weight table rather than from `is_current`.
 */

export type RangeWeightsTable =
  | "ba_cbam_fund_weights"
  | "ba_r_cbam_fund_weights"
  | "ba_dist_cbam_fund_weights"
  | "ba_mm_cbam_fund_weights";

/** Weight rows are equal-to-zero within this tolerance. Matches the exit test in the trade paths. */
export const WEIGHT_EPSILON = 0.0001;

export interface WeightRow {
  fund_id: string;
  portfolio: number;
  weight_pct: number | string | null;
  effective_date: string;
}

export type FundPickerGroup =
  /** Held in this range right now (latest-effective weight > 0). */
  | "held"
  /** Has weight history in this range but the latest row is zero — exited, and re-buyable. */
  | "previous"
  /** No history in this range, but active elsewhere — a legitimate new addition to this range. */
  | "other";

export interface RangeFund {
  id: string;
  fund_name: string;
  fund_manager: string | null;
  fund_class: string | null;
  isin: string | null;
  is_current: boolean;
  heldInRange: boolean;
  previouslyHeldInRange: boolean;
  group: FundPickerGroup;
}

/**
 * Latest-effective weight per `fund_id|portfolio`, optionally bounded to rows dated on or before
 * `asOf`.
 *
 * The `asOf` bound is what stops a back-dated trade from being resolved against rows that are in
 * its own future. Without it, a sell back-dated to March is tested against today's weights and
 * can stamp a zero IN FRONT of later rows that still show the fund held.
 */
export function latestWeights(rows: WeightRow[], asOf?: string): Map<string, number> {
  const best = new Map<string, { weight: number; date: string }>();
  for (const r of rows) {
    if (asOf && r.effective_date > asOf) continue;
    const key = `${r.fund_id}|${r.portfolio}`;
    const prev = best.get(key);
    if (!prev || r.effective_date > prev.date) {
      best.set(key, { weight: Number(r.weight_pct) || 0, date: r.effective_date });
    }
  }
  const out = new Map<string, number>();
  for (const [key, v] of best) out.set(key, v.weight);
  return out;
}

/** Sort by manager, then fund name, then class — the ordering used by the management tables. */
function byManagerThenName(a: RangeFund, b: RangeFund): number {
  const m = (a.fund_manager || "").localeCompare(b.fund_manager || "", undefined, { sensitivity: "base" });
  if (m !== 0) return m;
  const f = (a.fund_name || "").localeCompare(b.fund_name || "", undefined, { sensitivity: "base" });
  if (f !== 0) return f;
  return (a.fund_class || "").localeCompare(b.fund_class || "", undefined, { sensitivity: "base" });
}

const GROUP_ORDER: Record<FundPickerGroup, number> = { held: 0, previous: 1, other: 2 };

export const FUND_GROUP_LABEL: Record<FundPickerGroup, string> = {
  held: "Currently held in this range",
  previous: "Previously held — buy back",
  other: "Not held here (held in another range)",
};

/** Human-readable name for a picker row. */
export function fundPickerLabel(f: RangeFund): string {
  return `${f.fund_manager ? `${f.fund_manager} — ` : ""}${f.fund_name}${f.fund_class ? ` (${f.fund_class})` : ""}`;
}

export interface UseRangeFundsResult {
  /** Every fund row, annotated against this range. */
  all: RangeFund[];
  /** What a trade dialog should offer: held + previously held + active elsewhere. */
  selectable: RangeFund[];
  /** Exited holdings of this range only — the buy-back candidates. */
  previouslyHeld: RangeFund[];
  /** Raw weight rows for this range, for callers that need an as-of view. */
  weightRows: WeightRow[];
  isLoading: boolean;
}

/**
 * Funds annotated against one range's weight table.
 *
 * Query key keeps the historic `"ba-cbam-funds-active"` prefix on purpose: react-query matches
 * invalidations by key prefix, so the existing
 * `invalidateQueries({ queryKey: ["ba-cbam-funds-active"] })` calls elsewhere in the app (e.g.
 * RHypotheticalCalculator.tsx:886, which is outside this change) still refresh these pickers. The
 * table name is appended because the annotations are range-specific and must not be shared
 * between the four trade logs the way the old single cache entry was.
 *
 * Weight tables are small (P 448, R 293, MM 40, Dist 0 rows as of 2026-08-10), so pulling them
 * whole and reducing client-side stays well inside PostgREST's 1000-row default cap. UNVERIFIED
 * whether that holds indefinitely; if any range grows past ~1000 rows this needs a server-side
 * "latest per fund/portfolio" view instead.
 */
export function useRangeFunds(weightsTable: RangeWeightsTable): UseRangeFundsResult {
  const { data, isLoading } = useQuery({
    queryKey: ["ba-cbam-funds-active", weightsTable, "v2-reactivate"],
    queryFn: async () => {
      const [fundsRes, weightsRes] = await Promise.all([
        supabase
          .from("ba_cbam_funds")
          .select("id, fund_name, fund_manager, fund_class, isin, is_current")
          .order("fund_name"),
        supabase.from(weightsTable).select("fund_id, portfolio, weight_pct, effective_date"),
      ]);
      if (fundsRes.error) throw fundsRes.error;
      if (weightsRes.error) throw weightsRes.error;
      return {
        funds: (fundsRes.data || []) as {
          id: string;
          fund_name: string;
          fund_manager: string | null;
          fund_class: string | null;
          isin: string | null;
          is_current: boolean | null;
        }[],
        weights: ((weightsRes.data || []) as unknown) as WeightRow[],
      };
    },
  });

  const funds = data?.funds || [];
  const weightRows = data?.weights || [];

  const latest = latestWeights(weightRows);
  const heldIds = new Set<string>();
  const historyIds = new Set<string>();
  for (const r of weightRows) historyIds.add(r.fund_id);
  for (const [key, w] of latest) {
    if (w > WEIGHT_EPSILON) heldIds.add(key.split("|")[0]);
  }

  const all: RangeFund[] = funds.map((f) => {
    const heldInRange = heldIds.has(f.id);
    const previouslyHeldInRange = !heldInRange && historyIds.has(f.id);
    return {
      id: f.id,
      fund_name: f.fund_name,
      fund_manager: f.fund_manager,
      fund_class: f.fund_class,
      isin: f.isin,
      is_current: !!f.is_current,
      heldInRange,
      previouslyHeldInRange,
      group: heldInRange ? "held" : previouslyHeldInRange ? "previous" : "other",
    };
  });

  const selectable = all
    .filter((f) => f.heldInRange || f.previouslyHeldInRange || f.is_current)
    .sort((a, b) => GROUP_ORDER[a.group] - GROUP_ORDER[b.group] || byManagerThenName(a, b));

  const previouslyHeld = all.filter((f) => f.previouslyHeldInRange).sort(byManagerThenName);

  return { all, selectable, previouslyHeld, weightRows, isLoading };
}

/** Group an ordered picker list into its display sections, preserving order and dropping empties. */
export function groupFunds(funds: RangeFund[]): { group: FundPickerGroup; label: string; funds: RangeFund[] }[] {
  const groups: FundPickerGroup[] = ["held", "previous", "other"];
  return groups
    .map((g) => ({ group: g, label: FUND_GROUP_LABEL[g], funds: funds.filter((f) => f.group === g) }))
    .filter((s) => s.funds.length > 0);
}
